import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authLogin, authRegister, authGetMe, setPermissionRefreshCallback } from './api';

export interface UserOrg {
  org_id: string;
  org_name: string;
}

export interface AuthUser {
  id: string;
  email: string;
  full_name: string;
  is_active: boolean;
  is_admin: boolean;
  role_id: string | null;
  role_name: string | null;
  permissions: string[];
  created_at: string | null;
  organizations: UserOrg[];
}

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  activeOrg: UserOrg | null;
  setActiveOrg: (org: UserOrg) => void;
  hasPermission: (permission: string) => boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, full_name: string, password: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeOrg, setActiveOrgState] = useState<UserOrg | null>(null);

  const setActiveOrg = useCallback((org: UserOrg) => {
    setActiveOrgState(org);
    localStorage.setItem('active_org_id', org.org_id);
  }, []);

  const _resolveActiveOrg = useCallback((orgs: UserOrg[]) => {
    if (!orgs || orgs.length === 0) return null;
    const storedId = localStorage.getItem('active_org_id');
    if (storedId) {
      const match = orgs.find((o) => o.org_id === storedId);
      if (match) return match;
    }
    localStorage.setItem('active_org_id', orgs[0].org_id);
    return orgs[0];
  }, []);

  const hasPermission = useCallback(
    (permission: string): boolean => {
      if (!user) return false;
      if (user.is_admin) return true;
      return user.permissions?.includes(permission) ?? false;
    },
    [user]
  );

  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const res = await authGetMe();
      const userData: AuthUser = res.data;
      setUser(userData);
      const org = _resolveActiveOrg(userData.organizations || []);
      setActiveOrgState(org);
    } catch {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('active_org_id');
      setUser(null);
      setActiveOrgState(null);
    } finally {
      setLoading(false);
    }
  }, [_resolveActiveOrg]);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  useEffect(() => {
    setPermissionRefreshCallback(refreshUser);
  }, [refreshUser]);

  const login = async (email: string, password: string) => {
    const res = await authLogin({ email, password });
    localStorage.setItem('access_token', res.data.access_token);
    localStorage.setItem('refresh_token', res.data.refresh_token);
    const userData: AuthUser = res.data.user;
    setUser(userData);
    const org = _resolveActiveOrg(userData.organizations || []);
    setActiveOrgState(org);
  };

  const register = async (email: string, full_name: string, password: string) => {
    const res = await authRegister({ email, full_name, password });
    localStorage.setItem('access_token', res.data.access_token);
    localStorage.setItem('refresh_token', res.data.refresh_token);
    const userData: AuthUser = res.data.user;
    setUser(userData);
    const org = _resolveActiveOrg(userData.organizations || []);
    setActiveOrgState(org);
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('active_org_id');
    setUser(null);
    setActiveOrgState(null);
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, activeOrg, setActiveOrg, hasPermission, login, register, logout, refreshUser }}
    >
      {children}
    </AuthContext.Provider>
  );
};
